import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowsClockwise, ArrowRight } from "@phosphor-icons/react";
import API from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const WINDOWS = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "0", label: "All time" },
];

const SORTS = [
  { value: "clicks", label: "Sort: Clicks" },
  { value: "conversions", label: "Sort: Conversions" },
  { value: "revenue", label: "Sort: Revenue" },
  { value: "cvr", label: "Sort: CVR" },
];

function NetPill({ network }) {
  const n = (network || "").toLowerCase();
  if (!n) return <span className="text-slate-400 text-xs">—</span>;
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-bold uppercase tracking-wider">
      {n === "pubadhub_kevin" ? "pah-kevin" : n}
    </span>
  );
}

function Stat({ label, value, sub, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-1.5 tabular-nums">{value}</div>
      {sub ? <div className="text-xs text-slate-500 mt-1">{sub}</div> : null}
    </div>
  );
}

/** Table-shaped placeholder — the page keeps its layout while loading
 *  instead of flashing empty, which reads as "no data". */
function TableSkeleton({ rows = 8 }) {
  return (
    <div className="p-3 space-y-2" data-testid="routed-perf-skeleton">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-14 ml-auto" />
          <Skeleton className="h-4 w-14" />
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </div>
  );
}

export default function RoutedPerformancePanel() {
  const [days, setDays] = useState("30");
  const [sort, setSort] = useState("clicks");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Only the newest request may write state. Switching the window to
  // "All time" and back to 7 days fires two requests; the heavier one
  // can land second and paint all-time numbers under a 7-day label.
  const reqId = useRef(0);

  const fetchData = useCallback(async () => {
    const mine = ++reqId.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({ days, sort });
      const { data: d } = await API.get(`/admin/traffic-remaps/performance?${params.toString()}`);
      if (mine !== reqId.current) return;
      setData(d);
    } catch (e) {
      if (mine !== reqId.current) return;
      toast.error(e?.response?.data?.detail || "Could not load routed traffic performance");
      setData(null);
    } finally {
      if (mine === reqId.current) setLoading(false);
    }
  }, [days, sort]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const t = data?.totals;
  const items = data?.items || [];

  return (
    <div data-testid="routed-performance-panel">
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <select
          value={days}
          onChange={(e) => setDays(e.target.value)}
          className="border border-slate-200 rounded px-2.5 py-1.5 text-sm bg-white"
          data-testid="routed-perf-window"
        >
          {WINDOWS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
        </select>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="border border-slate-200 rounded px-2.5 py-1.5 text-sm bg-white"
          data-testid="routed-perf-sort"
        >
          {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <Button size="sm" variant="outline" onClick={fetchData} disabled={loading} data-testid="routed-perf-refresh">
          <ArrowsClockwise size={14} className="mr-1.5" /> {loading ? "Loading…" : "Refresh"}
        </Button>
        <p className="text-xs text-slate-500 ml-auto max-w-xl">
          Only clicks a rule actually diverted. Conversions are joined from{" "}
          <code className="text-[11px]">actions</code> by click id — not from{" "}
          <code className="text-[11px]">click_log.converted</code>, which stays false for
          networks whose sync does not write back. Reversed conversions are excluded;
          revenue is settled (approved) money, with unsettled shown separately.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        {loading && !t ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-lg p-4">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-7 w-16 mt-2" />
            </div>
          ))
        ) : (
          <>
            <Stat label="Brands routed" value={t?.brands ?? 0} testId="routed-perf-brands" />
            <Stat label="Clicks diverted" value={(t?.clicks ?? 0).toLocaleString()} testId="routed-perf-clicks" />
            <Stat
              label="Conversions"
              value={(t?.conversions ?? 0).toLocaleString()}
              sub={
                (t?.conversions_pending || t?.conversions_reversed)
                  ? `${t?.conversions_pending ?? 0} pending · ${t?.conversions_reversed ?? 0} reversed (excluded)`
                  : null
              }
              testId="routed-perf-conversions"
            />
            <Stat label="CVR" value={`${t?.cvr ?? 0}%`} testId="routed-perf-cvr" />
            <Stat
              label="Revenue (settled)"
              value={`$${(t?.revenue ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
              sub={`$${(t?.revenue_pending ?? 0).toFixed(2)} pending · pub earning $${(t?.earning ?? 0).toFixed(2)}`}
              testId="routed-perf-revenue"
            />
          </>
        )}
      </div>

      {!loading && t && t.clicks > 0 && t.conversions === 0 && t.clicks < 100 ? (
        <div className="mb-5 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900" data-testid="routed-perf-sample-note">
          <strong>{t.clicks} clicks is too small to judge.</strong> At a normal 1–3% conversion
          rate you would need 100–300 before expecting a single conversion, so zero here says
          nothing about how the destinations perform. Route brands that carry real volume to get
          a readable number.
        </div>
      ) : null}

      {data?.by_network?.length ? (
        <div className="mb-5 flex flex-wrap gap-2" data-testid="routed-perf-by-network">
          {data.by_network.map((b) => (
            <div key={b.to_network} className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs">
              <NetPill network={b.to_network} />
              <span className="ml-2 text-slate-700 tabular-nums">
                {b.clicks.toLocaleString()} clicks · {b.conversions} conv · ${b.revenue.toFixed(2)}
                {b.revenue_pending ? ` (+$${b.revenue_pending.toFixed(2)} pending)` : ""}
              </span>
              <span className="ml-1.5 text-slate-400">({b.brands} brands)</span>
            </div>
          ))}
        </div>
      ) : null}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Source brand</th>
                <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Routed to</th>
                <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Clicks</th>
                <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Conv</th>
                <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">CVR</th>
                <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Revenue</th>
                <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Last click</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-0"><TableSkeleton /></td></tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-10 text-center text-sm text-slate-500" data-testid="routed-perf-empty">
                    No traffic has been diverted in this window yet. Rules only show here once a
                    click actually flows through them.
                  </td>
                </tr>
              ) : (
                items.map((r) => (
                  <tr key={`${r.from_brand_id}|${r.to_network}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-3 py-2.5">
                      <div className="font-medium text-slate-900">{r.from_brand_name || r.from_brand_id}</div>
                      <div className="text-[11px] text-slate-400 font-mono">{r.from_brand_id}</div>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <NetPill network={r.from_network} />
                        <ArrowRight size={12} className="text-slate-400" />
                        <NetPill network={r.to_network} />
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{r.to_brand_name}</div>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-900">{r.clicks.toLocaleString()}</td>
                    <td className={`px-3 py-2.5 text-right tabular-nums font-medium ${r.conversions > 0 ? "text-emerald-700" : "text-slate-400"}`}>
                      {r.conversions}
                      {r.conversions_reversed ? (
                        <span className="block text-[10px] font-normal text-rose-600" title="Reversed by the network — not counted above">
                          {r.conversions_reversed} rev
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{r.cvr}%</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-900">
                      ${r.revenue.toFixed(2)}
                      {r.revenue_pending ? (
                        <span className="block text-[10px] font-normal text-slate-400" title="Pending — not yet settled">
                          +${r.revenue_pending.toFixed(2)}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-500 text-xs">{r.last_click || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
